import { Tabs } from "antd";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import apiFactory from "../../../api";
import { useChatContext } from "../../../context/ChatContext";
import { useMenuContext } from "../../../context/MenuContext";
import { FileItem, ImageItem } from "./PanelView";

function ArchiveView({ jumpToMessage, resourceViewer }) {
  const { selectedConversation } = useMenuContext();
  const { resourcesArchive, setResourcesArchive, archiveType, setArchiveType } =
    useChatContext();

  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const [hasMoreBottom, setHasMoreBottom] = useState(true);

  const MAX_DAYS = 5;

  const formatDateOnly = (isoString) =>
    new Date(isoString).toISOString().split("T")[0];

  const addDays = (date, days) => {
    const copy = new Date(date);
    copy.setDate(copy.getDate() + days);
    return copy;
  };

  const loadResources = useCallback(
    async (direction, pivotCreatedAt) => {
      if (!selectedConversation?.conversationId) return;
      try {
        setLoading(true);

        const result = await apiFactory.resourceApi.getResourcesArchiveType({
          conversationId: selectedConversation.conversationId,
          fileType: archiveType,
          pivotCreatedAt,
          direction,
          limit: MAX_DAYS,
        });

        if (!result || result.length === 0) {
          setHasMoreBottom(false);
          return;
        }

        if (direction === "next") {
          setResourcesArchive((prev) => [...prev, ...result]);
        }
      } catch (err) {
        console.error("Error load resources:", err);
      } finally {
        setLoading(false);
      }
    },
    [archiveType, selectedConversation?.conversationId]
  );

  useEffect(() => {
    setHasMoreBottom(true);
    setResourcesArchive([]);
    const tomorrow = addDays(new Date(), 1);
    loadResources("next", formatDateOnly(tomorrow));
  }, [archiveType, selectedConversation?.conversationId, loadResources]);

  useEffect(() => {
    if (!bottomRef.current) return;

    let timeoutId = null;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !loading && hasMoreBottom) {
            const last = resourcesArchive[resourcesArchive?.length - 1];
            if (last?.date) {
              clearTimeout(timeoutId);
              timeoutId = setTimeout(() => {
                loadResources("next", last.date);
              }, 300);
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px 300px 0px" }
    );

    observer.observe(bottomRef.current);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [resourcesArchive, loading, hasMoreBottom, loadResources]);

  return (
    <>
      <Tabs
        activeKey={archiveType}
        onChange={(key) => setArchiveType(key)}
        items={[
          { key: "IMAGE", label: "Images" },
          { key: "FILE", label: "Files/Videos" },
          // { key: "LINK", label: "Links" },
        ]}
      />
      <div className="h-full overflow-auto">
        {resourcesArchive?.map((daily) => (
          <div
            className="p-4 pt-2 bg-white border-b-[8px] border-[#eaeef0]"
            key={daily?.date}
          >
            <div className="font-medium text-gray-600 mb-2">{daily?.date}</div>
            {archiveType === "IMAGE" && (
              <div className="grid grid-cols-3 gap-2">
                {daily?.files?.map((file) =>
                  file?.photos?.map((photo, i) => (
                    <ImageItem
                      idx={i}
                      file={{ ...file, media: photo }}
                      jumpToMessage={jumpToMessage}
                      openViewer={() =>
                        resourceViewer?.handleOpenViewer(
                          {
                            ...photo,
                            createdAt: file?.createdAt,
                            userId: file?.senderId,
                          },
                          file?.createdAt
                        )
                      }
                    />
                  ))
                )}
              </div>
            )}
            {archiveType === "FILE" && (
              <div className="flex flex-col gap-2">
                {daily?.files?.map((file, idx) => (
                  <FileItem
                    key={file?.id || idx}
                    idx={idx}
                    file={file}
                    jumpToMessage={jumpToMessage}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
        {resourcesArchive?.length === 0 && !loading && (
          <p className="p-4 text-sm text-gray-500">No resources yet</p>
        )}
        <div ref={bottomRef} style={{ height: 1 }} />
      </div>
    </>
  );
}

export default memo(ArchiveView);
